import { CSSProperties, useState, useRef, MouseEventHandler, useEffect, Ref } from 'react';
import classNames from 'classnames';
import { useClickAnyWhere } from 'usehooks-ts';
import { Link } from 'react-router-dom';

import { useClientRect } from '@/lib/hooks/use-client-rect';
import { useKeyPress } from '@/lib/hooks/use-key-press';

import { CheckboxWithLabel, MixedCheckboxWithLabel } from '../form/checkbox';
import { Overlay } from '../overlay';
import { RadioButton } from '../form/radio-button';
import { ColorPicker } from '../form/color-input';

import { Tooltip } from '../tooltip';

import styles, { dropdownTriggerGap } from './dropdown.module.scss';

export const DropdownIconSizePx = 14;
const HoverCloseDelayMs = 150;
const DropdownTriggerGapPx = parseInt(dropdownTriggerGap, 10);

const useDropdownKeyPress = (key: string, handler: () => void, isOpen: boolean) => {
  useKeyPress(
    key,
    (e: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handler();
    },
    { capture: true, includeInputs: true },
  );
};

export interface DropdownMenuItemActionable {
  label: string | React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: MouseEventHandler;
  onMouseOver?: MouseEventHandler;
  onMouseDown?: MouseEventHandler;
  className?: classNames.Argument;
  color?: string;
  focused?: boolean;
  tooltip?: string;
}

export interface DropdownMenuItemButton extends DropdownMenuItemActionable {
  type?: 'button';
}

interface DropdownMenuItemLink extends DropdownMenuItemActionable {
  type: 'link';
  href: string;
  external?: boolean;
}

export interface DropdownMenuItemCheckbox extends DropdownMenuItemActionable {
  type: 'checkbox';
  checked?: boolean;
  onChange: (isChecked: boolean) => void;
}
export interface DropdownMenuItemMixedCheckbox extends DropdownMenuItemActionable {
  type: 'mixed-checkbox';
  onClick: () => void;
}

interface DropdownMenuItemRadio extends DropdownMenuItemActionable {
  type: 'radio';
  checked?: boolean;
}

interface DropdownMenuItemColor extends DropdownMenuItemActionable {
  type: 'color';
  label: string;
  color: string;
  presetColors?: string[];
  onChange: (color: string | undefined) => void;
}

export interface DropdownMenuItemDivider {
  type: 'divider';
}

export interface DropdownMenuItemHeader {
  type: 'header';
  label: string;
}

export type DropdownMenuItem =
  | DropdownMenuItemButton
  | DropdownMenuItemLink
  | DropdownMenuItemCheckbox
  | DropdownMenuItemMixedCheckbox
  | DropdownMenuItemRadio
  | DropdownMenuItemDivider
  | DropdownMenuItemHeader
  | DropdownMenuItemColor;

type DropdownProps = {
  items: DropdownMenuItem[];
  trigger: (isOpen: boolean, setIsOpen: (isOpen: boolean) => void) => React.ReactNode;
  align: 'left' | 'center' | 'right' | 'both';
  animated?: boolean;
  openOnHover?: boolean;
  className?: classNames.Argument;
  menuClassName?: classNames.Argument;
  style?: CSSProperties;
};

export const Dropdown = (props: DropdownProps) => {
  const { items, trigger, align, className, style, animated = true, openOnHover = false } = props;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const triggerRect = useClientRect(triggerRef, [isOpen]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRect = useClientRect(dropdownRef, [isOpen]);
  const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelHoverClose = () => {
    if (hoverCloseTimeoutRef.current !== undefined) {
      clearTimeout(hoverCloseTimeoutRef.current);
      hoverCloseTimeoutRef.current = undefined;
    }
  };

  const handleHoverEnter = () => {
    if (!openOnHover) {
      return;
    }
    cancelHoverClose();
    setIsOpen(true);
  };

  const handleHoverLeave = () => {
    if (!openOnHover) {
      return;
    }
    cancelHoverClose();
    hoverCloseTimeoutRef.current = setTimeout(() => setIsOpen(false), HoverCloseDelayMs);
  };

  useEffect(
    () => () => {
      if (hoverCloseTimeoutRef.current !== undefined) {
        clearTimeout(hoverCloseTimeoutRef.current);
      }
    },
    [],
  );

  useClickAnyWhere((e) => {
    const inTrigger = triggerRef.current?.contains(e.target as Node) ?? false;
    const inDropdown = dropdownRef.current?.contains(e.target as Node) ?? false;

    if (!isOpen || inTrigger || inDropdown) {
      return;
    }
    setIsOpen(false);
  });

  useKeyPress(
    'Escape',
    (e: KeyboardEvent) => {
      if (isOpen) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
      }
    },
    { capture: true, includeInputs: true },
  );

  const handleMenuClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    setIsOpen(false);
  };

  const shouldOpenAbove = () =>
    getLowerAnchorY() + getDropdownHeight() > window.innerHeight &&
    getUpperAnchorY() - getDropdownHeight() > 0;

  const getLowerAnchorY = () => triggerRect?.bottom ?? 0;
  const getUpperAnchorY = () => triggerRect?.top ?? 0;
  const getAnchorY = () => (shouldOpenAbove() ? getUpperAnchorY() : getLowerAnchorY());

  const getDropdownHeight = () => dropdownRect?.height ?? 0;
  const getViewportHeight = () => window.innerHeight - DropdownTriggerGapPx * 2;

  // When positioning below the trigger, we shift the dropdown up if it would overflow the viewport
  const getYOverflow = () => Math.max(0, getAnchorY() + getDropdownHeight() - getViewportHeight());

  const getUpperY = () => getUpperAnchorY() - getDropdownHeight();
  const getLowerY = () => Math.max(0, getAnchorY() - getYOverflow());
  const getCenteredX = () => {
    const centeredX =
      (triggerRect?.left ?? 0) + ((triggerRect?.width ?? 0) - (dropdownRect?.width ?? 0)) / 2;
    const maximumX = Math.max(
      DropdownTriggerGapPx,
      window.innerWidth - (dropdownRect?.width ?? 0) - DropdownTriggerGapPx,
    );
    return Math.min(maximumX, Math.max(DropdownTriggerGapPx, centeredX));
  };

  const openAbove = shouldOpenAbove();
  const dropdownPos =
    triggerRect === null || dropdownRect === null
      ? undefined
      : {
          top: openAbove ? getUpperY() : getLowerY(),
          left:
            align === 'right'
              ? triggerRect.right - dropdownRect.width
              : align === 'center'
                ? getCenteredX()
                : triggerRect.left,
          minWidth: align === 'both' ? triggerRect.width : 'auto',
        };

  return (
    <div
      className={classNames(styles.dropdown, className, {
        [styles.dropdownOpen]: isOpen,
      })}
      style={style}>
      <div
        ref={triggerRef}
        className={styles.triggerContainer}
        onMouseEnter={handleHoverEnter}
        onMouseLeave={handleHoverLeave}>
        {trigger(isOpen, setIsOpen)}
      </div>

      {isOpen && (
        <Overlay>
          <DropdownMenu
            ref={dropdownRef}
            items={items}
            animated={animated}
            className={props.menuClassName}
            style={dropdownPos}
            closeDropdown={handleMenuClick}
            isOpen={isOpen}
            openAbove={openAbove}
            onMouseEnter={handleHoverEnter}
            onMouseLeave={handleHoverLeave}
          />
        </Overlay>
      )}
    </div>
  );
};

export function DropdownMenu({
  ref,
  items: itemsProp,
  className,
  style,
  animated,
  closeDropdown,
  isOpen = true,
  openAbove = false,
  onMouseEnter,
  onMouseLeave,
}: {
  ref?: Ref<HTMLDivElement>;
  items: DropdownMenuItem[];
  className?: classNames.Argument;
  style?: CSSProperties;
  animated?: boolean;
  closeDropdown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  isOpen?: boolean;
  openAbove?: boolean;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
}) {
  const [keyboardFocusedIndex, setKeyboardFocusedIndex] = useState<number>(-1);
  const focusedItemRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  const isItemFocusable = (item: DropdownMenuItem): boolean => {
    if (item.type === 'divider' || item.type === 'header') {
      return false;
    }
    if ('disabled' in item && item.disabled === true) {
      return false;
    }
    return true;
  };

  const getNextFocusableIndex = (currentIndex: number): number => {
    const afterCurrent = itemsProp.slice(currentIndex + 1).findIndex(isItemFocusable);
    if (afterCurrent !== -1) {
      return currentIndex + 1 + afterCurrent;
    }

    const fromStart = itemsProp.slice(0, currentIndex + 1).findIndex(isItemFocusable);
    return fromStart !== -1 ? fromStart : currentIndex;
  };

  const getPrevFocusableIndex = (currentIndex: number): number => {
    const beforeCurrent = itemsProp.slice(0, currentIndex).findLastIndex(isItemFocusable);
    if (beforeCurrent !== -1) {
      return beforeCurrent;
    }

    const fromEnd = itemsProp.slice(currentIndex).findLastIndex(isItemFocusable);
    return fromEnd !== -1 ? currentIndex + fromEnd : currentIndex;
  };

  const getFirstFocusableIndex = () => itemsProp.findIndex(isItemFocusable);
  const getLastFocusableIndex = () => itemsProp.findLastIndex(isItemFocusable);

  useDropdownKeyPress(
    'ArrowDown',
    () => {
      setKeyboardFocusedIndex((prev) =>
        prev < 0 ? getFirstFocusableIndex() : getNextFocusableIndex(prev),
      );
    },
    isOpen,
  );

  useDropdownKeyPress(
    'ArrowUp',
    () => {
      setKeyboardFocusedIndex((prev) =>
        prev < 0 ? getLastFocusableIndex() : getPrevFocusableIndex(prev),
      );
    },
    isOpen,
  );

  useDropdownKeyPress('Enter', () => focusedItemRef.current?.click(), isOpen);
  useDropdownKeyPress(' ', () => focusedItemRef.current?.click(), isOpen);

  useEffect(() => {
    if (!isOpen) {
      setKeyboardFocusedIndex(-1);
    }
  }, [isOpen]);

  const items = itemsProp.map((item, idx) => {
    if (item.type === 'divider' || item.type === 'header') {
      return item;
    }

    const existingFocused = 'focused' in item && item.focused === true;
    const isFocused = existingFocused || idx === keyboardFocusedIndex;

    return {
      ...item,
      focused: isFocused,
    };
  });

  const focusedItemIndex = items.findIndex((item) => 'focused' in item && item.focused);

  useEffect(() => {
    focusedItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [focusedItemIndex]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Clear focus when mouse moves over empty space (not over any item)
    if (!(e.target as HTMLElement).closest(`.${styles.item}`)) {
      setKeyboardFocusedIndex(-1);
    }
  };

  return (
    <div
      ref={ref}
      className={classNames(
        styles.dropdownMenu,
        {
          [styles.animated]: animated,
          [styles.openAbove]: openAbove,
        },
        className,
      )}
      style={style}
      onMouseMove={handleMouseMove}
      onMouseEnter={onMouseEnter}
      onMouseLeave={(event) => {
        setKeyboardFocusedIndex(-1);
        onMouseLeave?.(event);
      }}>
      {items.map((item, idx) => {
        const isFocused = 'focused' in item && item.focused;
        if ('tooltip' in item) {
          return (
            <Tooltip key={idx} content={item.tooltip} placement="right">
              <div>
                <DropdownMenuItem
                  item={item}
                  itemIndex={idx}
                  ref={isFocused ? focusedItemRef : null}
                  closeDropdown={closeDropdown}
                  setFocusedIndex={setKeyboardFocusedIndex}
                />
              </div>
            </Tooltip>
          );
        }

        return (
          <DropdownMenuItem
            key={idx}
            item={item}
            itemIndex={idx}
            ref={isFocused ? focusedItemRef : null}
            closeDropdown={closeDropdown}
            setFocusedIndex={setKeyboardFocusedIndex}
          />
        );
      })}
    </div>
  );
}

function DropdownMenuItem({
  ref,
  item,
  itemIndex,
  closeDropdown,
  setFocusedIndex,
}: {
  ref: React.Ref<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>;
  item: DropdownMenuItem;
  itemIndex: number;
  closeDropdown?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  setFocusedIndex?: (index: number) => void;
}) {
  const colorPickerRef = useRef<HTMLDivElement>(null);

  if (item.type === 'divider') {
    return <hr className={styles.divider} />;
  }

  if (item.type === 'header') {
    return <p className={styles.header}>{item.label}</p>;
  }

  const sharedProps = {
    className: classNames(styles.item, item.className, {
      [styles.disabled]: item.disabled,
      [styles.focused]: item.focused === true,
    }),
    style: { color: item.color },
    onClick: (e: React.MouseEvent) => {
      item.onClick?.(e);
      if (item.disabled !== true) {
        closeDropdown?.(e as React.MouseEvent<HTMLDivElement, MouseEvent>);
      }
    },
    onMouseMove: () => {
      if (item.disabled !== true) {
        setFocusedIndex?.(itemIndex);
      } else {
        setFocusedIndex?.(-1);
      }
    },
    onMouseOver: item.onMouseOver,
    onMouseDown: item.onMouseDown,
    tabIndex: -1,
  };

  switch (item.type) {
    case 'link':
      return (
        <Link
          {...sharedProps}
          to={item.href}
          {...(item.external === true && { target: '_blank', rel: 'noopener noreferrer' })}
          ref={ref as React.Ref<HTMLAnchorElement>}>
          {item.icon !== undefined && item.icon}
          {item.label}
        </Link>
      );
    case 'checkbox':
      return (
        <CheckboxWithLabel
          {...sharedProps}
          ref={ref as React.Ref<HTMLLabelElement>}
          checked={item.checked}
          onChange={({ checked }) => item.onChange?.(checked)}
          onClick={(e) => e.stopPropagation()}>
          {item.icon !== undefined && item.icon}
          {item.label}
        </CheckboxWithLabel>
      );
    case 'mixed-checkbox':
      return (
        <MixedCheckboxWithLabel
          {...sharedProps}
          ref={ref as React.Ref<HTMLLabelElement>}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick?.();
          }}>
          {item.label}
        </MixedCheckboxWithLabel>
      );
    case 'radio':
      return (
        <button
          {...sharedProps}
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          onClick={(e) => {
            e.stopPropagation();
            item.onClick?.(e);
            if (item.disabled !== true) {
              closeDropdown?.(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
            }
          }}>
          <RadioButton
            name={typeof item.label === 'string' ? item.label : ''}
            label={item.label}
            isChecked={item.checked}
          />
        </button>
      );
    case 'color':
      return (
        <div
          className={classNames(styles.item, styles.colorPickerItem, {
            [styles.focused]: item.focused === true,
          })}
          onMouseMove={sharedProps.onMouseMove}
          ref={(el) => {
            if (el) {
              el.click = () => colorPickerRef.current?.click();
            }
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
          }}
          onClick={(e) => e.stopPropagation()}>
          <ColorPicker
            ref={colorPickerRef}
            color={item.color}
            label={item.label}
            size="small"
            className={styles.colorPicker}
            presetColors={item.presetColors}
            onChange={item.onChange}
          />
        </div>
      );
    default:
      return (
        <button
          {...sharedProps}
          type="button"
          disabled={item.disabled}
          ref={ref as React.Ref<HTMLButtonElement>}>
          {item.icon !== undefined && item.icon}
          {item.label}
        </button>
      );
  }
}
